// IMP-223: PCActivationPanel 결정 로직 SSoT (순수 함수).
//
// BUG-113 PR #240 review (Arch Minor): 두 병렬 decision SSoT (code-based badge +
// PC-seat-based buttons) 가 컴포넌트 내 분산. 본 모듈로 외부화하여 우선순위를 명확화하고
// 단위 테스트로 동작을 고정한다. (컴포넌트는 resolver 결과만 분기에 사용)
//
// 우선순위(uiState): RAYTeams 설치/로그인/로딩 → unique-seat 한도 → READY 카드.
// READY 카드 내부는 직교(orthogonal) 가드들(code 차원 / seat 차원 / user info)을 플래그로 반환.

// IMP-221: 차단코드 정의는 lib/licenseCodes SSoT 로 이전. 내부(isActivationBlocked) 사용을
// 위해 로컬 바인딩으로 import 하고, 기존 import 경로 호환을 위해 동일 이름으로 re-export.
import { ACTIVATION_BLOCKED_CODES, isActivationBlocked } from './licenseCodes';

export { ACTIVATION_BLOCKED_CODES, isActivationBlocked };

// 동시 좌석 한도 / unique PC 한도 (rayteams 정책 SSoT).
// IMP-205: metric(activations/activeSeats/seatCount) 정의·BE→FE 매핑·규칙 근거는
//   docs/spec/pc-activation-metrics.md 참조. 아래 상수 변경 시 해당 문서도 함께 갱신.
export const MAX_CONCURRENT_SEATS = 2;
export const MAX_UNIQUE_PCS = 5;

/**
 * PC 활성화 패널의 결정 상태를 산출한다 (순수).
 *
 * @param {object} input
 * @param {boolean|null} input.rayteamsInstalled  RAYTeams 설치 여부 (null=미확인/로딩)
 * @param {object|null}  input.rayteamsUser        RAYTeams 로그인 사용자 (null=미로그인)
 * @param {object|null}  input.localLicenseStatus  rayteams ?purpose=check 응답 (code/check/sk/guid)
 * @param {Array}        input.pcActivations       BE /pcs 활성 좌석 목록
 * @param {object|null}  input.matchedVoucher      FE 스코프 내 매칭 voucher
 * @param {number}       input.activationCount      누적 활성화 횟수 (unique 한도용)
 * @param {boolean}      input.pcActivationLoading  /pcs 로딩 중
 * @param {object|null}  input.pcActivationError    /pcs 에러
 * @returns {{
 *   uiState: 'NOT_INSTALLED'|'NOT_LOGGED_IN'|'LOADING'|'MAX_UNIQUE_REACHED'|'READY',
 *   action: 'activate'|'deactivate'|'select-and-activate'|'none',
 *   activeCount: number, uniqueLimitCount: number,
 *   isActivatedForThisVoucher: boolean, isNotActivated: boolean, seatFull: boolean,
 *   uniqueLimitReached: boolean, hasOtherLicenseInstalled: boolean,
 *   isVoucherUnregistered: boolean, isLicenseStatusPending: boolean,
 *   isActivationCodeBlocked: boolean, isActivationUiVisible: boolean,
 * }}
 */
export const resolvePcActivationState = ({
    rayteamsInstalled,
    rayteamsUser,
    localLicenseStatus,
    pcActivations,
    matchedVoucher,
    activationCount,
    pcActivationLoading,
    pcActivationError,
}) => {
    const list = Array.isArray(pcActivations) ? pcActivations : [];
    const activePcs = list.filter((pc) => !pc.revoke);
    const activeCount = activePcs.length;
    const uniqueLimitCount = typeof activationCount === 'number' ? activationCount : 0;

    // Seat 점유: BE 활성 목록에 이 PC 의 guid(rtid) 존재 여부 (License check 와 무관).
    const thisPcRtid = localLicenseStatus?.guid;
    const thisPcInActiveList = !!thisPcRtid && list.some((pc) => pc.rtid === thisPcRtid && !pc.revoke);

    // pcActivations 가 BE 로부터 정상 도달했는지 — 로딩/에러 시 판단 보류(false 강제 방지).
    const pcActivationsReady = !pcActivationLoading && !pcActivationError;
    const isActivatedForThisVoucher = pcActivationsReady && thisPcInActiveList;

    // 다른 라이선스 설치 안내 (seat 무관). 이 voucher 로 이미 활성이면 의미상 미표시.
    const isExternalScope = !matchedVoucher;
    const isCrossVoucher = !!matchedVoucher && localLicenseStatus?.sk !== matchedVoucher.sk;
    const hasOtherLicenseInstalled = (
        pcActivationsReady
        && localLicenseStatus?.check === true
        && !!localLicenseStatus?.sk
        && (isExternalScope || isCrossVoucher)
        && !isActivatedForThisVoucher
    );

    const isNotActivated = pcActivationsReady && !isActivatedForThisVoucher;
    const seatFull = activeCount >= MAX_CONCURRENT_SEATS;
    const uniqueLimitReached = pcActivationsReady
        && uniqueLimitCount >= MAX_UNIQUE_PCS
        && !isActivatedForThisVoucher;

    // code 차원 가드 (BE SSoT) — 직교.
    const isVoucherUnregistered = localLicenseStatus?.code === 'N0003';
    const isLicenseStatusPending = !!matchedVoucher && !localLicenseStatus;
    const isActivationCodeBlocked = isActivationBlocked(localLicenseStatus?.code);
    const isActivationUiVisible = !!matchedVoucher
        && !isLicenseStatusPending
        && !isVoucherUnregistered
        && !isActivationCodeBlocked;

    // ── uiState 우선순위 (early-return 순서와 일치) ──
    let uiState;
    if (rayteamsInstalled === false) {
        uiState = 'NOT_INSTALLED';
    } else if (rayteamsInstalled === true && rayteamsUser === null) {
        uiState = 'NOT_LOGGED_IN';
    } else if (rayteamsInstalled === null) {
        uiState = 'LOADING';
    } else if (uniqueLimitReached) {
        uiState = 'MAX_UNIQUE_REACHED';
    } else {
        uiState = 'READY';
    }

    // ── action (READY + 활성화 UI 표시 시 의미) ──
    let action = 'none';
    if (isActivationUiVisible) {
        if (isActivatedForThisVoucher) action = 'deactivate';
        else if (isNotActivated && !seatFull) action = 'activate';
        else if (isNotActivated && seatFull) action = 'select-and-activate';
    }

    return {
        uiState,
        action,
        activePcs,
        activeCount,
        uniqueLimitCount,
        isExternalScope,
        isActivatedForThisVoucher,
        isNotActivated,
        seatFull,
        uniqueLimitReached,
        hasOtherLicenseInstalled,
        isVoucherUnregistered,
        isLicenseStatusPending,
        isActivationCodeBlocked,
        isActivationUiVisible,
    };
};

export default resolvePcActivationState;
